import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AuditTrail = ({ journeyId, token }) => {
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAuditLog();
  }, [journeyId, token]);

  const fetchAuditLog = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `http://localhost:5000/api/journeys/${journeyId}/audit-log`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setAuditLog(response.data.audit_log || []);
      setError('');
    } catch (err) {
      console.error('Audit log fetch error:', err);
      setError('Failed to load audit trail');
    } finally {
      setLoading(false);
    }
  };

  const getEventIcon = (eventType) => {
    const icons = {
      journey_activated: '🚀',
      intake_submitted: '📥',
      framing_submitted: '🎯',
      hypotheses_submitted: '💭',
      experiment_submitted: '🔬',
      decision_submitted: '✅',
      idea_submitted: '💡',
      comment_added: '💬',
      rating_submitted: '⭐',
    };
    return icons[eventType] || '📌';
  };

  const getEventLabel = (eventType) => {
    const labels = {
      journey_activated: 'Journey Activated',
      intake_submitted: 'Intake Submitted',
      framing_submitted: 'Framing Submitted',
      hypotheses_submitted: 'Hypotheses Defined',
      experiment_submitted: 'Experiment Results Submitted',
      decision_submitted: 'Decision Recorded',
      idea_submitted: 'Idea Submitted',
      comment_added: 'Comment Added',
      rating_submitted: 'Rating Submitted',
    };
    return labels[eventType] || eventType;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>Loading audit trail...</div>;
  }

  return (
    <div className="tab-panel audit-trail-panel">
      <div className="audit-trail-section">
        <h3>Journey Timeline & Audit Trail</h3>
        <p style={{ color: '#666', marginBottom: '30px' }}>
          Complete record of all actions and decisions throughout this innovation journey
        </p>

        {error && <div className="form-error">{error}</div>}

        {auditLog.length === 0 ? (
          <p style={{ color: '#999', textAlign: 'center', padding: '40px' }}>
            No events recorded yet. Start by submitting your intake.
          </p>
        ) : (
          <div className="audit-timeline">
            {auditLog.map((entry, index) => (
              <div key={index} className="audit-entry">
                <div className="audit-icon">
                  {getEventIcon(entry.event_type)}
                </div>
                <div className="audit-content">
                  <div className="audit-header">
                    <h4>{getEventLabel(entry.event_type)}</h4>
                    <span className="audit-date">
                      {formatDate(entry.created_at)}
                    </span>
                  </div>
                  <p className="audit-user">
                    {entry.name || 'System'} ({entry.role || 'unknown'})
                  </p>
                  {entry.details && (
                    <div className="audit-details">
                      {typeof entry.details === 'string' ? (
                        <p>{entry.details}</p>
                      ) : (
                        <ul>
                          {Object.entries(entry.details).map(([key, value]) => (
                            <li key={key}>
                              <strong>{key}:</strong> {String(value).substring(0, 100)}
                              {String(value).length > 100 ? '...' : ''}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {auditLog.length > 0 && (
          <div style={{ marginTop: '30px', padding: '20px', background: '#f9f9f9', borderRadius: '8px' }}>
            <h4>Journey Summary</h4>
            <div className="summary-stats">
              <div className="stat">
                <div className="stat-value">{auditLog.length}</div>
                <div className="stat-label">Total Events</div>
              </div>
              <div className="stat">
                <div className="stat-value">
                  {new Set(auditLog.map(e => e.user_id)).size}
                </div>
                <div className="stat-label">Team Members</div>
              </div>
              <div className="stat">
                <div className="stat-value">
                  {auditLog[auditLog.length - 1] ?
                    (() => {
                      const first = new Date(auditLog[0].created_at);
                      const last = new Date(auditLog[auditLog.length - 1].created_at);
                      const days = Math.ceil((last - first) / (1000 * 60 * 60 * 24));
                      return days || '<1';
                    })()
                    : '—'
                  }
                </div>
                <div className="stat-label">Days Duration</div>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .audit-trail-panel {
          background: #fff;
        }

        .audit-trail-section {
          padding: 30px;
        }

        .audit-trail-section h3 {
          font-size: 1.3em;
          margin-bottom: 10px;
          color: #2c3e50;
        }

        .audit-timeline {
          position: relative;
          padding: 20px 0;
        }

        .audit-timeline::before {
          content: '';
          position: absolute;
          left: 30px;
          top: 0;
          bottom: 0;
          width: 2px;
          background: linear-gradient(to bottom, #8FC5B2, #E8E8E8);
        }

        .audit-entry {
          display: flex;
          gap: 20px;
          margin-bottom: 30px;
          position: relative;
        }

        .audit-icon {
          flex-shrink: 0;
          width: 60px;
          height: 60px;
          background: #f9f9f9;
          border: 2px solid #8FC5B2;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          z-index: 1;
        }

        .audit-content {
          flex: 1;
          padding: 15px 20px;
          background: #f9f9f9;
          border-radius: 8px;
          border-left: 3px solid #8FC5B2;
        }

        .audit-header {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          gap: 15px;
          margin-bottom: 8px;
        }

        .audit-header h4 {
          margin: 0;
          color: #2c3e50;
          font-size: 0.95em;
        }

        .audit-date {
          font-size: 0.85em;
          color: #95a5a6;
          white-space: nowrap;
        }

        .audit-user {
          margin: 0 0 10px 0;
          font-size: 0.9em;
          color: #7f8c8d;
        }

        .audit-details {
          margin-top: 10px;
          padding-top: 10px;
          border-top: 1px solid #e0e0e0;
        }

        .audit-details p,
        .audit-details li {
          margin: 5px 0;
          font-size: 0.9em;
          color: #34495e;
          line-height: 1.5;
        }

        .audit-details ul {
          list-style: none;
          padding: 0;
        }

        .audit-details li {
          padding-left: 0;
        }

        .summary-stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
          margin-top: 15px;
        }

        .stat {
          text-align: center;
        }

        .stat-value {
          font-size: 28px;
          font-weight: 700;
          color: #8FC5B2;
          margin-bottom: 5px;
        }

        .stat-label {
          font-size: 12px;
          color: #999;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        @media (max-width: 768px) {
          .audit-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .summary-stats {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default AuditTrail;
